import classNames from 'classnames'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { InScopeController } from 'legacy/lut/components/widgets/public/partner360/components/inScopeController/InScopeController'
import { TRANSLATION_KEYS } from 'legacy/lut/components/widgets/public/partner360/offeringsSwithList/translations'
import { WidgetHeader } from 'legacy/lut/components/widgets/public/partner360/widgetHeader/WidgetHeader'
import { IProgramType, selectOutOfScope } from 'legacy/lut/features/partner360/configuration'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Nullable } from 'clickNgo/models/utils'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'
import { getAvailableProgramTypes } from 'partner360/helpers/offerings/getAvailableProgramTypes'
import { getOfferingsInfo } from 'partner360/helpers/offerings/getOfferingsInfo'

import { IOfferingChange, OfferingSwitcher } from './OfferingSwitcher'

import styles from './OfferingsSwitchList.module.scss'

export interface ITogglesIsDisabledCond {
  enabledOfferings: IProgramType[]
  offering: IProgramType
}
interface IProps {
  onToggleOffering: (data: IOfferingChange) => void
  onToggleScopeValue?: (outOfScope: number) => void
  programTypes: IProgramType[]
  togglesIsDisabledCond?: ({ enabledOfferings }: ITogglesIsDisabledCond) => boolean
  scopeControllerIsDisabled?: boolean
  needShowWarning?: boolean
}

export const OfferingsSwitchList = ({
  onToggleOffering,
  programTypes,
  togglesIsDisabledCond,
  onToggleScopeValue,
  scopeControllerIsDisabled,
  needShowWarning,
}: IProps) => {
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [offeringForToggle, setOfferingForToggle] = useState<Nullable<IOfferingChange>>(null)

  const availableProgramTypes = useMemo(() => {
    return getAvailableProgramTypes(programTypes)
  }, [programTypes])

  const { currentOfferings, enabledOfferings, oldOfferings } = useMemo(() => {
    return getOfferingsInfo(availableProgramTypes)
  }, [availableProgramTypes])

  const outOfScope = useSelector(selectOutOfScope)

  const onToggle = useCallback(
    (data: IOfferingChange) => {
      if (needShowWarning && data.flag) {
        setShowWarningModal(true)
        setOfferingForToggle(data)
        return
      }

      onToggleOffering(data)
    },
    [needShowWarning, onToggleOffering]
  )

  const modalConfirm = useCallback(() => {
    if (offeringForToggle) {
      onToggleOffering(offeringForToggle)
      setOfferingForToggle(null)
      setShowWarningModal(false)
    }
  }, [offeringForToggle, onToggleOffering])

  const modalCancel = useCallback(() => {
    setOfferingForToggle(null)
    setShowWarningModal(false)
  }, [])

  return (
    <div className={styles.root}>
      <RenderWhen condition={onToggleScopeValue != undefined}>
        <InScopeController
          disabled={scopeControllerIsDisabled}
          notInScope={outOfScope}
          onChange={onToggleScopeValue!}
        />
      </RenderWhen>

      <WidgetHeader>jbp-partner-program-types-title</WidgetHeader>
      <div className={classNames(styles.group, styles.specialisationEditorWrapper)}>
        {currentOfferings.map((offering, index) => {
          const isLastEnabledOffering = enabledOfferings.length === 1 && offering.enabled
          const isTooltipDisabled = !isLastEnabledOffering
          const isToggleDisabled = togglesIsDisabledCond?.({ enabledOfferings, offering }) || false
          return (
            <OfferingSwitcher
              isToggleDisabled={isToggleDisabled}
              isTooltipDisabled={isTooltipDisabled}
              offering={offering}
              onToggle={onToggle}
              index={index}
              key={index}
              availableProgramTypes={availableProgramTypes}
            />
          )
        })}
        <RenderWhen condition={oldOfferings.length > 0}>
          <WidgetHeader>jbp-partner-old-program-types-title</WidgetHeader>
          {oldOfferings.map((offering, index) => {
            return (
              <OfferingSwitcher
                isToggleDisabled={true}
                isTooltipDisabled={true}
                offering={offering}
                onToggle={onToggle}
                index={index}
                key={index}
                availableProgramTypes={availableProgramTypes}
              />
            )
          })}
        </RenderWhen>
      </div>
      <ConfirmationDialog
        title={TRANSLATION_KEYS.warningDialog.title}
        content={TRANSLATION_KEYS.warningDialog.text}
        confirmBtnText={TRANSLATION_KEYS.warningDialog.ok}
        rejectBtnText={TRANSLATION_KEYS.warningDialog.cancel}
        onConfirm={modalConfirm}
        onReject={modalCancel}
        isVisible={showWarningModal}
      />
    </div>
  )
}
